import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, isDevMode } from '@angular/core';
import { ContentService, ServicePathProviderService } from '@blk/cwp-ng-framework';
import { AREAS_TO_EXPLORE_CONFIG_TOKEN } from '@ddt-web/config';
import { CellData, CellRow, CellUtil, ColumnConfig, ColumnText, ComponentConfig, ComponentData, ComponentDataFactory, ErrorData, ErrorDataOptions, ExtractorConfig, TabData, TableData } from '@ddt-web/data-service';
import { ConditionData, FundsToExploreGroup, OutputResult, RulesEngine } from '@ddt-web/rules-engine';
import { chain } from 'lodash';
import * as cloneDeep from 'lodash/cloneDeep';
import * as get from 'lodash/get';
import * as isObject from 'lodash/isObject';
import * as max from 'lodash/max';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { AreasToExploreConfig } from '../models/areas-to-explore-config.interface';


@Injectable({
	providedIn: 'root'
})
export class AreasToExploreDataService extends ExtractorConfig implements ComponentDataFactory {
	private readonly LOGGER_PREFIX = `[${AreasToExploreDataService.name}]`;

	private apiGatewayPath: string;
	private ateConfig: AreasToExploreRulesConfig;
	private debugMode: boolean = false; // Change this variable if we want to point to the local ate-mock-rules.json

	constructor(
		private rulesEngine: RulesEngine,
		private http: HttpClient,
		private servicePathProvider: ServicePathProviderService,
		@Inject(AREAS_TO_EXPLORE_CONFIG_TOKEN) private appSpecificAteConfig: AreasToExploreConfig
	) {
		super(null, null);
		// TODO: find a way to make this work
		// this.debugMode = get(this.urlService, 'queryParams.useLocalAteConfig', false);
		this.apiGatewayPath = this.servicePathProvider.getServicePath('api-gateway');
		this.retrieveConfig().subscribe(ateConfig => {
			this.ateConfig = ateConfig;
		});
	}

	/**
	 * Unimplemented - inheriting to get access to parent methods
	 *
	 * // DO NOT USE or write code
	 */
	public storeDependencies(): string[] {
		throw new Error('Method not implemented.');
	}
	/**
	 * Unimplemented - inheriting to get access to parent methods
	 * // DO NOT USE or write code
	 */
	public extract(storeLookup: Map<any, any>, contentSvc: ContentService): TableData[] {
		throw new Error('Method not implemented.');
	}

	public getComponentId(): string {
		return this.appSpecificAteConfig.componentId;
	}

	public storeConfigDependencies(): any[] {
		const stores = this.appSpecificAteConfig.storeList;
		return stores;
	}

	// separate getting config and actual comp data for more specific error handling
	public getComponentData(data: Map<string, any[]>, contentSvc: ContentService): ComponentData {
		console.log(this.LOGGER_PREFIX, 'Generating Areas to Explore Data');
		// Call Rules Engine
		const portfolioRules = this.getOutputFromRules(this.ateConfig.rulesConfigData, data, contentSvc);

		const tableDataPerTypeForRules = this.generateTableDataPerType(portfolioRules, data);
		const tabDataForRules = new TabData(this.appSpecificAteConfig.ruleConfig.RULES, tableDataPerTypeForRules);

		const tabDatas = [tabDataForRules, ...this.generateTabDatas(portfolioRules, data, contentSvc)];

		return new ComponentData(this.getComponentId(), tabDatas, this.getPortfolioData(data, contentSvc), false, null, true);
	}

	private generateTabDatas(portfolioRules: AreasToExploreRulesOutput[], data: Map<string, any[]>, contentSvc: ContentService): TabData[] {
		// Get widget types from output['widgets']
		const widgetTypes = chain(portfolioRules)
			.map(rulesPerPortfolio => rulesPerPortfolio.map(output => output['widgets']))
			.flattenDeep()
			.uniq()
			.value();

		return chain(widgetTypes.filter(widgetType => this.componentConfigBuilder().getTabStartWithId('areas-to-explore.' + widgetType)))
			.map(widgetType =>
				this.componentConfigBuilder().getTabStartWithId('areas-to-explore.' + widgetType).map(tabConfig => {
					try {
						const tableData: TableData[] = tabConfig.extractorConfig.extract(data, contentSvc);
						return new TabData(tabConfig, tableData);
					} catch (error) {
						console.warn('error occurred during extracting tab for areas-to-explore-data.service:' + tabConfig.id + ' with stacked trace: ' + (<Error>error).message);
						return new TabData(
							tabConfig,
							[],
							null,
							ErrorData.create({
								id: tabConfig.id,
								success: false,
								allDataReturned: false,
								errorCodes: ['TAB_EXTRACTOR_ERROR'],
								failureReason: 'Error while extracting data',
								debugMessage: 'Error with message:' + (<Error>error).message
							} as ErrorDataOptions)
						);
					}
				})
			)
			.flattenDeep()
			.uniq()
			.value();
	}

	private generateTableDataPerType(portfolioRules: any[], data: Map<string, any[]>): TableData[] {
		// Get output keys from evaluated rules
		const rulesOutputKeys = this.getOutputKeysFromRules(portfolioRules);

		// headersX column names will be the keys from the output generated from rules
		const headersX = rulesOutputKeys.map(ruleOutputKey => new CellData(null, ruleOutputKey));

		// headersY will be the portfolio names
		const headersY = data.get(this.appSpecificAteConfig.detailsStore).map(portfolioData => new CellData(null, portfolioData['name'] || portfolioData['description']));

		// cells will be values from the rules output
		const tableDataPerType: TableData[] = [];
		for (let i = 0; i < get(max(portfolioRules, portfolioRule => portfolioRule.length), 'length'); i++) {
			const cells = portfolioRules.map(rulesPerPortfolio => {
				const rulesForType = rulesPerPortfolio[i] ? [rulesPerPortfolio[i]] : [];
				if (rulesForType.length) {
					// get value for each key
					return rulesOutputKeys.map(key => {
						const cellConfig = new ColumnConfig(new ColumnText(key), null, null, key);
						const cellRawValue = rulesForType[0][key];
						const cellFormattedValue = isObject(cellRawValue) ? JSON.stringify(cellRawValue) : cellRawValue;
						return new CellData(cellConfig, cellRawValue, cellFormattedValue);
					});
				} else {
					return [];
				}
			});
			tableDataPerType.push(new TableData(headersX, headersY, cells));
		}

		// Log
		tableDataPerType.forEach(table => table.log());

		return tableDataPerType;
	}

	private getOutputKeysFromRules(portfolioRules: any[]): any {
		return chain(portfolioRules)
			.map(rulesPerPortfolio => rulesPerPortfolio.map(rules => Object.keys(rules)))
			.flattenDeep()
			.uniq()
			.value();
	}

	private getOutputFromRules(ruleData: ConditionData[], data: Map<string, any[]>, contentSvc: ContentService): AreasToExploreRulesOutput[] {
		const portfoliosCount = data.get(this.appSpecificAteConfig.detailsStore).length;
		const storesCombined: Array<any> = Array.from({ length: portfoliosCount }, () => new Object()); // [{details: {}, cap: {}}, {details: {}, cap: {}}]

		data.forEach((portfolios, key) => {
			portfolios.forEach((portfolio, index) => {
				storesCombined[index][key] = data.get(key)[index];
			});
		});
		this.rulesEngine.updateStoreWithCustomDataPoints(storesCombined, contentSvc, this.appSpecificAteConfig.factMapping, 'ate');

		return storesCombined.map(portfolioData => {
			const ruleDataTemplate = cloneDeep(ruleData); // deep copy the ruleData
			const rulesEngineOutput: OutputResult[] = this.rulesEngine.run(ruleDataTemplate, portfolioData);
			const rules = chain(rulesEngineOutput)
				.filter((output: OutputResult) => {
					if (output.error) {
						console.warn(output.error);
					}
					return !output.error;
				})
				.map(output => output.output)
				.orderBy(output => (this.ateConfig.initialInsightOrderRank.indexOf(output.type) || 0) + Number(get(output, 'offSetRank', 0)))
				.value();
			console.log(this.LOGGER_PREFIX, 'rules', rules);

			if (this.ateConfig.maxInsights) {
				rules.splice(this.ateConfig.maxInsights);
				console.log(this.LOGGER_PREFIX, 'rules spliced', rules);
			}

			return rules;
		});
	}

	private retrieveConfig(): Observable<AreasToExploreRulesConfig> {
		if (isDevMode && this.debugMode) {
			
			const tempResponse = require(`../assets/dummy-backend-data/${this.appSpecificAteConfig.mockRules}`);
			console.warn(this.LOGGER_PREFIX, 'Using mock data rules');
			return of(tempResponse as AreasToExploreRulesConfig);
		} else {
			return this.http.get(this.apiGatewayPath + this.appSpecificAteConfig.configPath).pipe(
				map(jsonResponse => get(jsonResponse, 'resultMap.REPORT', [])),
				map((rules: any[]) => {
					return {
						maxInsights: get(rules.find(rule => get(rule, 'variant') === 'default'), 'data.maxInsights', 0),
						rulesConfigData: rules.filter(rule => get(rule, 'variant') !== 'default').map(filteredRules => get(filteredRules, 'data', {})),
						initialInsightOrderRank: rules.filter(rule => get(rule, 'variant') !== 'default').map(filteredRules => get(filteredRules, 'variant', ''))
					} as AreasToExploreRulesConfig;
				}),
				catchError(error => {
					console.error('Areas-to-explore rules config loading error:', error);
					return of({ maxInsights: 0, rulesConfigData: [] } as AreasToExploreRulesConfig);
				})
			);
		}
	}

	/**
	 * Please keep this method as a contract with the rest of the framework
	 * @param data
	 * @param contentSvc
	 */
	private getPortfolioData(data: Map<string, any[]>, contentSvc: ContentService): CellRow[] {
		const portfolioDataColumns = [...this.appSpecificAteConfig.portfolioColumnsWithACBenchmark];
		return chain(
			portfolioDataColumns.map(column => {
				return this.extractStoreColumn(column, data, contentSvc);
			})
		)
			.unzip()
			.value()
			.map(portfolioCells => CellUtil.getCellRow(portfolioCells));
	}

	private componentConfigBuilder(): ComponentConfig {
		return new ComponentConfig('areas-to-explore', Object.values(this.appSpecificAteConfig.ruleConfig));
	}
}

export interface AreasToExploreRulesConfig {
	maxInsights: number;
	rulesConfigData: ConditionData[];
	initialInsightOrderRank?: string[];
}

export interface AreasToExploreRulesOutput {
	sectionHeading: string;
	subheading: string;
	rank: number;
	type: string;
	observation: string;
	biiView: string;
	widgets: string[];
	fundsToExploreText: string;
	fundsToExplore: FundsToExploreGroup[];
}
